import { isDesktopBreakpoint } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * A custom element that manages a hero title.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} logo – The logo element.
 *
 * @extends Component<Refs>
 */
class HeroTitle extends Component {
  requiredRefs = ['logo'];
  /** @type {number} */
  scrollDistance = 0;
  /** @type {number} */
  toScale = 0;
  /** @type {number | null} */
  toTranslateX = null;

  /** @type {number} */
  targetHeight = 0;
  /** @type {number} */
  targetTop = 0;
  /** @type {number | null} */
  targetLeft = null;

  /** @type {number | null} */
  animationFrameId = null;

  /** @type {number} */
  windowWidth = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setTargetSize();
    this.#updateTransform();
    this.windowWidth = window.innerWidth;

    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  #handleScroll = () => {
    if (this.scrollDistance <= 0) return;
    this.animationFrameId = requestAnimationFrame(this.#updateTransform);
  };

  #handleResize = () => {
    if (this.windowWidth === window.innerWidth) return;
    this.windowWidth = window.innerWidth;
    this.#resetStyles();
    this.#setTargetSize();
    this.animationFrameId = requestAnimationFrame(this.#updateTransform);
  };

  #resetStyles() {
    this.refs.logo.style.transform = '';
  }

  #updateTransform = () => {
    const clampedScroll = Math.max(0, Math.min(window.scrollY, this.scrollDistance));
    const scaleRatio = (1 - this.toScale) / this.scrollDistance;
    const scale = 1 - scaleRatio * clampedScroll;

    if (this.toTranslateX) {
      const leftRatio = this.toTranslateX / this.scrollDistance;
      const left = (leftRatio * clampedScroll) / scale;
      this.refs.logo.style.transform = `scale(${Math.round(scale * 1000) / 1000}) translateX(${Math.round(left * 1000) / 1000}px)`;
    } else {
      this.refs.logo.style.transform = `scale(${Math.round(scale * 1000) / 1000})`;
    }

    if (window.scrollY < this.scrollDistance) {
      this.classList.remove('hero-title--top');
    } else {
      this.classList.add('hero-title--top');
    }
  }

  #setTargetSize() {
    const { height: containerHeight } = this.getBoundingClientRect();

    const logoMiddle = containerHeight / 2;
    const { height: startHeight, left: startLeft } = this.refs.logo.getBoundingClientRect();

    if (isDesktopBreakpoint()) {
      this.targetHeight = 25;
      this.targetTop = 17.5;
      this.targetLeft = null;
    } else {
      this.targetHeight = 42;
      this.targetTop = 12;
      this.targetLeft = 16;
    }

    this.scrollDistance = Math.round(logoMiddle - this.targetTop - this.targetHeight / 2);
    this.toScale = Math.round((this.targetHeight / startHeight) * 100) / 100;
    if (this.targetLeft !== null) {
      this.toTranslateX = Math.round(this.targetLeft - startLeft);
    }
  }
}

if (!customElements.get('hero-title')) {
  customElements.define('hero-title', HeroTitle);
}
